import { useStore } from '../../app/store';
import { fetchProgressForLearnersOrNull } from './linkedProgress';
import { createResetState } from '../../config/initialState';
import { mergeRemoteIntoLocal } from './soloProgressSync';

/**
 * Haalt de voortgang van een aantal leerlingen van de server en zet die in de
 * lokale store, zodat de bestaande schermen hem zien.
 *
 * ── Waarom dit bestaat ────────────────────────────────────────────────────
 *
 * Voortgang staat in `solo_progress` op de server. Elk scherm dat voortgang
 * toont, leest `multiAccountState.learnerData[id]` — de lokale kopie. Op het
 * apparaat van het KIND vult die zichzelf; op het apparaat van een ouder of
 * leerkracht is hij leeg, want daar heeft dat kind nooit geoefend.
 *
 * Sinds migratie 0016 mag een gekoppelde ouder én de leerkracht van de klas die
 * rij lezen (`can_view_learner()`). Dit is de projectie daarvan.
 *
 * Bewust één functie voor beide portalen. Het ouderportaal had zijn eigen
 * variant en het leerkrachtportaal geen enkele, met als gevolg dat een
 * leerkracht 0% beheersing zag voor een kind dat thuis gewoon oefende. Twee
 * kopieën zouden dat verschil opnieuw laten ontstaan zodra er één wordt
 * bijgewerkt.
 *
 * ── Wat hier NIET in zit ──────────────────────────────────────────────────
 *
 * Toetsuitslagen en rapporten. Die vallen onder de toestemmingsmuur van 0010 en
 * staan voor een solo-kind uitsluitend op zijn eigen apparaat. Een leerkracht
 * ziet dus oefenvoortgang, geen toetsgeschiedenis.
 *
 * @returns `false` alleen wanneer de serveraanroep MISLUKTE. Leerlingen die nog
 * geen voortgang hebben leveren `true` — dan is 0% een feit en geen gok. Een
 * scherm dat die twee niet onderscheidt, meldt 0% als bevinding over een kind.
 */
export async function hydrateLearnerProgress(learnerIds: string[]): Promise<boolean> {
  if (learnerIds.length === 0) return true;
  const progressByLearner = await fetchProgressForLearnersOrNull(learnerIds);
  // `null` = de query mislukte. Een LEGE uitkomst is iets anders: dan hebben
  // deze leerlingen echt nog niets gedaan, en is 0% een feit dat gewoon
  // getoond mag worden. Die twee door elkaar halen zou de waarschuwing laten
  // opduiken bij elke nieuwe klas — en een waarschuwing die vaak onterecht is,
  // wordt genegeerd op het moment dat ze klopt.
  if (progressByLearner === null) return false;
  if (Object.keys(progressByLearner).length === 0) return true;
  const s = useStore.getState();
  if (!s.multiAccountState) return false;
  const learnerData = {
    ...(s.multiAccountState.learnerData ?? {})
  };
  const names = s.multiAccountState.accounts?.accountsById ?? {};
  for (const id of learnerIds) {
    const remote = progressByLearner[id];
    if (!remote) continue;
    // `createResetState` levert de lege basis. Op het apparaat van een
    // leerkracht bestaat er per definitie geen lokale staat voor dit kind, en
    // de merge moet iets hebben om ín te schrijven.
    const displayName = (names[id] as {
      displayName?: string;
    } | undefined)?.displayName ?? 'Leerling';
    const base = learnerData[id] ?? createResetState(displayName);
    learnerData[id] = mergeRemoteIntoLocal(base as never, remote) as never;
  }
  useStore.setState({
    multiAccountState: {
      ...s.multiAccountState,
      learnerData
    }
  });
  return true;
}
